import React, { FunctionComponent, useState } from 'react';
import { Button, Row, Col, Form, Input, Alert, Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { Link, useHistory } from 'react-router-dom';
import { Store } from 'antd/es/form/interface';
import { observer } from 'mobx-react';
import { ValidatedFormItem } from 'Components/validated-form-item';
import { LoginRequestSchema } from 'Schemas';
import { useFormValidation, useService, useStores } from 'Hooks';
import { AuthenticationService } from 'Services/AuthenticationService';
import { images } from 'variant';
import './index.less';

const Login: FunctionComponent = observer(() => {
    const { t } = useTranslation();
    const antIcon = <LoadingOutlined style={{ fontSize: 24 }} spin />;
    const [errors, validateForm] = useFormValidation(LoginRequestSchema);
    const authenticationService = useService(AuthenticationService);
    const [loginErrorState, setLoginErrorState] = useState();
    const [spinner, setSpinner] = useState(false);
    const { authorizationStore } = useStores();

    const history = useHistory();
    const hasResetPassword = history.location.state;
    //clear history state after consuming so that re-render does not keep state.
    window.history.replaceState(null, '');

    async function submit(values: Store): Promise<void> {
        const valid = await validateForm(values, false);
        if (!valid) return;
        try {
            setSpinner(true);
            await authenticationService.initAccessToken(values.username, values.password);
        } catch (error) {
            if (error.response && error.response.status === 400)
                setLoginErrorState(t('User.email_password_not_exist'));
            else setLoginErrorState(t('Errors.unexpected_error'));
        } finally {
            setSpinner(false);
        }
    }

    return (
        <>
            <Row className="mt-20" gutter={[0, loginErrorState ? 30 : 40]}>
                <Col span={4} />
                <Col span={16}>
                    <img id="logo" src={images.logoWhite} alt="logo" />
                </Col>
                <Col span={4} />
            </Row>
            {loginErrorState && (
                <Row gutter={[0, 40]}>
                    <Col span={12} offset={6}>
                        <Alert className="login-error" message={loginErrorState} type="error" />
                    </Col>
                </Row>
            )}
            {hasResetPassword && (
                <Row gutter={[0, 40]}>
                    <Col span={12} offset={6}>
                        <Alert
                            className="password-reset"
                            message={t('User.password_email_sent')}
                            type="error"
                        />
                    </Col>
                </Row>
            )}
            {authorizationStore.userIsNotManager && (
                <Row gutter={[0, 40]}>
                    <Col span={12} offset={6}>
                        <Alert
                            className="login-error"
                            message={t('Errors.forbidden')}
                            type="error"
                        />
                    </Col>
                </Row>
            )}

            <Row gutter={[0, 80]}>
                <Col span={12} offset={6}>
                    <Form
                        layout="vertical"
                        onFinish={async (values): Promise<void> => {
                            submit(values);
                        }}
                    >
                        <ValidatedFormItem
                            errors={errors}
                            label={t('User.email')}
                            name="username"
                            required
                        >
                            <Input maxLength={50} />
                        </ValidatedFormItem>

                        <ValidatedFormItem
                            errors={errors}
                            label={t('User.password')}
                            name="password"
                            required
                        >
                            <Input type="password" maxLength={50} />
                        </ValidatedFormItem>

                        {spinner && <Spin indicator={antIcon} size="large" />}

                        <p className="forgot-password">
                            <Link to="/forgot-Password">{t('User.forgot_password')}</Link>
                        </p>

                        {/* @TODO - Check/ follow how to resolve the findDOMNode warning issue with Ant and Strictmode */}
                        {/* See Open issue here: https://github.com/ant-design/ant-design/issues/22493 */}

                        <Button type="primary" htmlType="submit" className="login-btn">
                            {t('Log in')}
                        </Button>
                    </Form>
                </Col>
            </Row>
        </>
    );
});

export default Login;
